import { z } from 'zod';

export const UsernameSchema = z
	.string()
	.min(4, 'Usernames must be at least 4 characters long')
	.max(20, 'Usernames must be 20 characters or less');

export const UserRoleSchema = z.enum(['admin', 'user']);
export const UserIdSchema = z.string().uuid();
export const UserEmailSchema = z.string().email();
export const POLICY_ID_LENGTH = 56;

export const UserSchema = z.object({
	id: UserIdSchema,
	role: UserRoleSchema,
	created_at: z.date(),
	photo: z.string().url(),
	username: UsernameSchema
});

/**
 * For now, no consistent constraints have been identified since they can be
 * variable length. Not sure if there's an official min/max or character pattern
 * to match against.
 */
export const HexAddressSchema = z.string().min(1);

export const WalletSchema = z.object({
	hex_address: HexAddressSchema,
	bech32_address: z.string().optional(),
	notification_email: z.string().optional(),
	display_name: z.string().min(3).max(16).optional()
});

export type Wallet = z.infer<typeof WalletSchema>;

export type User = z.infer<typeof UserSchema>;

export type UserSupabase = Omit<User, 'created_at'> & { created_at: string };

// Sam said policyId is a blake2b_224 hash
// meaning it is always a 56 length hex (0-f) string
export const cardanoPolicyIdSchema = z
	.string()
	.length(POLICY_ID_LENGTH)
	.regex(/^[a-f0-9]+$/i);
export type CardanoPolicyId = z.infer<typeof cardanoPolicyIdSchema>;

// Unit string is policy ID + hex(asset name).
// Asset can only be 64 bytes long whether it's unencoded byte string or UTF-8. Hex
// chars hold 4 bits of info, so 128 hex chars is the limit.
// https://input-output-hk.github.io/cardano-wallet/user-guide/common-use-cases/TxMetadata#:~:text=Limits,protocol%20limit%20on%20transaction%20size.
export const cardanoUnitSchema = z.string().regex(/^[a-f0-9]{56,184}$/i);
export type CardanoUnit = z.infer<typeof cardanoUnitSchema>;

export const cardanoAssetIdSchema = z
	.string()
	.length(44)
	.regex(/(asset1)[a-zA-Z0-9]{38}/);
export type CardanoAssetId = z.infer<typeof cardanoAssetIdSchema>;
export const cardanoTokenNameSchema = z
	.string()
	.min(1)
	.max(128)
	.regex(/[a-fA-F0-9]{1,128}/);
export type CardanoTokenName = z.infer<typeof cardanoTokenNameSchema>;

export const cardanoBech32Schema = z.string().regex(/^(addr1|addr_test1)\w+$/i);
export type CardanoBech32Sechema = z.infer<typeof cardanoBech32Schema>;
